/**
 * PublicDiscoverSection — THE activation moment.
 *
 * Search-first cinematic experience. Works like an intelligent search bar:
 *   - Accepts bare company names ("stripe"), domains ("stripe.com"), or full URLs.
 *   - Resolves ambiguity with candidate picker.
 *   - Runs full Granullo Discover analysis on the selected candidate.
 *   - Shows result as dramatic program cards.
 *
 * Flow: IDLE → TYPING → CANDIDATES (if ambiguous) → ANALYZING → RESULT.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, ArrowRight, Loader2, Sparkles, Lightbulb,
  Building, Leaf, Train, Download, CheckCircle, AlertTriangle,
  Shield, Globe, X, TrendingUp,
} from 'lucide-react';
import api from '../../lib/api';
import SignalBreakdownBars from './SignalBreakdownBars';

const LENS_COLORS = {
  organization: '#6B6B6B',
  foundation: '#3D3D3D',
  rail: '#D97706',
};

const SECTOR_ICONS = {
  fintech: Train,
  banking: Building,
  payroll_hr: Building,
  climate_ngo: Leaf,
  healthcare: Shield,
  education: Lightbulb,
  government: Building,
  insurance: Shield,
  enterprise_saas: Globe,
  logistics: Train,
  nonprofit_foundation: Leaf,
  agriculture: Leaf,
};

const QUICK_EXAMPLES = [
  'stripe',
  'apollo hospitals',
  'azim premji foundation',
  'hdfc bank',
  'darwinbox',
];

// ─────────────────────────────────────────────────────────────────
// StructuralReadPanel — surfaces the new multi-axis structural read
// (topology + verticals + money-flow) so the user can see HOW Granullo
// understood the company, not just a single sector label.
// ─────────────────────────────────────────────────────────────────
function StructuralReadPanel({ structure }) {
  if (!structure) return null;
  const { topology = {}, top_verticals = [], top_money_flows = [], paired_roles = [] } = structure;

  const topologyChip = (() => {
    const ts = topology.two_sided || 0;
    const ns = topology.n_sided || 0;
    const ss = topology.single_sided || 0;
    if (ns >= 0.4) return { label: 'Multi-sided platform', score: ns };
    if (ts >= 0.4) return { label: 'Two-sided marketplace', score: ts };
    if (ss >= 0.4) return { label: 'Single-sided / direct', score: ss };
    return null;
  })();

  const cleanName = (k) => (k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const showVerticals = (top_verticals || []).filter((v) => v.score >= 0.3).slice(0, 3);
  const showFlows     = (top_money_flows || []).filter((v) => v.score >= 0.3).slice(0, 3);
  const showPairs     = (paired_roles || []).filter((p) => p.strength === 'paired').slice(0, 3);

  const hasAny = topologyChip || showVerticals.length || showFlows.length || showPairs.length;
  if (!hasAny) return null;

  const Chip = ({ children, tone = 'default', testid }) => (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-mono"
      style={{
        background: tone === 'scarlet' ? 'rgba(205,34,0,0.06)' : 'rgba(15,23,42,0.04)',
        color: tone === 'scarlet' ? '#CD2200' : '#3D3D3D',
        border: tone === 'scarlet' ? '1px solid rgba(205,34,0,0.18)' : '1px solid rgba(15,23,42,0.06)',
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </span>
  );

  return (
    <div
      className="mt-4 pt-4"
      style={{ borderTop: '1px solid rgba(15,23,42,0.04)' }}
      data-testid="discover-structural-read"
    >
      <div
        className="text-[10px] font-mono uppercase tracking-[0.18em] mb-3"
        style={{ color: '#6B6B6B' }}
      >
        Structural read
      </div>
      <div className="flex flex-wrap gap-1.5">
        {topologyChip && (
          <Chip tone="scarlet" testid="structural-topology">
            {topologyChip.label}
          </Chip>
        )}
        {showVerticals.map((v) => (
          <Chip key={`v-${v.name}`} testid={`structural-vertical-${v.name}`}>
            {cleanName(v.name)}
          </Chip>
        ))}
        {showFlows.map((f) => (
          <Chip key={`f-${f.name}`} testid={`structural-flow-${f.name}`}>
            {cleanName(f.name)} flow
          </Chip>
        ))}
      </div>
      {showPairs.length > 0 && (
        <div className="mt-2.5 text-[10.5px] leading-relaxed" style={{ color: '#6B6B6B' }}>
          <span className="font-mono uppercase tracking-[0.16em] text-[9.5px]" style={{ color: '#9A9A9A' }}>
            Paired participants
          </span>
          <span className="ml-2">
            {showPairs.map((p) => cleanName(p.vertical)).join(' · ')}
          </span>
        </div>
      )}
    </div>
  );
}

const SCARLET = '#CD2200';

export default function PublicDiscoverSection() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [resolving, setResolving] = useState(false);
  const [candidates, setCandidates] = useState(null); // {query, candidates, is_unique, best, fallback_domain}
  const [showCandidates, setShowCandidates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showOptIn, setShowOptIn] = useState(false);
  const [optInName, setOptInName] = useState('');
  const [optInEmail, setOptInEmail] = useState('');
  const [optedIn, setOptedIn] = useState(false);

  const inputRef = useRef(null);
  const candidateBoxRef = useRef(null);

  // Debounced resolve — typing feedback
  useEffect(() => {
    const q = input.trim();
    if (!q || q.length < 2 || result) {
      setCandidates(null);
      setShowCandidates(false);
      return;
    }
    // Don't resolve for "typing a URL" — wait for submit
    const looksLikeDomain = q.includes('://') || (/^[\w-]+\.[a-z]{2,}$/i).test(q);
    if (looksLikeDomain) {
      setCandidates(null);
      setShowCandidates(false);
      return;
    }
    setResolving(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/discover/resolve', { q });
        setCandidates(res.data);
        setShowCandidates((res.data.candidates || []).length > 0);
      } catch {
        setCandidates(null);
      } finally {
        setResolving(false);
      }
    }, 180);
    return () => clearTimeout(timer);
  }, [input, result]);

  // Close candidate dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (
        candidateBoxRef.current &&
        !candidateBoxRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowCandidates(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const runAnalyze = useCallback(async ({ url, text }) => {
    setLoading(true);
    setResult(null);
    setError(null);
    setShowCandidates(false);
    try {
      const payload = url ? { url, mode: 'auto' } : { text, mode: 'auto' };
      const res = await api.post('/discover/analyze', payload);
      setResult(res.data);
      api.post('/crm/journey', {
        domain: url || '',
        entity_name: res.data.entity_name || '',
        input_text: text || '',
        input_url: url || '',
        entity_type: res.data.entity_type || 'unknown',
        mode: 'auto',
        cards_shown: (res.data.cards || []).map((c) => c.archetype_id),
        card_count: res.data.card_count || 0,
        deep_mode: res.data.deep_mode || false,
        source: 'homepage',
      }).catch(() => {});
    } catch (e) {
      setError('Could not analyze. Try a company name or a website URL.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Submit handler — intelligently routes to resolve or analyze
  const handleSubmit = async () => {
    const q = input.trim();
    if (!q) return;

    const looksLikeFullUrl = q.includes('://');
    const looksLikeDomain = (/^[\w-]+\.[a-z]{2,}$/i).test(q);

    if (looksLikeFullUrl) {
      await runAnalyze({ url: q });
      return;
    }
    if (looksLikeDomain) {
      await runAnalyze({ url: `https://${q}` });
      return;
    }

    // Bare name — use the resolver result (already fetched) or fetch on demand
    let resolved = candidates;
    if (!resolved || resolved.query !== q) {
      setResolving(true);
      try {
        const res = await api.post('/discover/resolve', { q });
        resolved = res.data;
        setCandidates(res.data);
      } catch {
        setResolving(false);
        setError('Resolver is unavailable. Try again.');
        return;
      }
      setResolving(false);
    }

    if (resolved.candidates.length > 1 && !resolved.is_unique) {
      setShowCandidates(true);
      return;
    }
    if (resolved.best && resolved.best.domain) {
      await runAnalyze({ url: `https://${resolved.best.domain}` });
      return;
    }
    if (resolved.fallback_domain) {
      await runAnalyze({ url: `https://${resolved.fallback_domain}` });
      return;
    }
    // True free text — analyze as text
    await runAnalyze({ text: q });
  };

  const handlePickCandidate = async (cand) => {
    setInput(cand.entity_name || cand.domain);
    setShowCandidates(false);
    await runAnalyze({ url: `https://${cand.domain}` });
  };

  const handleCard = (card) => {
    const isUrl = input.startsWith('http') || /\./.test(input);
    const domain = isUrl ? input : '';
    api.post('/crm/event', { domain, action: 'selected', archetype_id: card.archetype_id }).catch(() => {});
    api.post('/crm/event', { domain, action: 'create_conversion', archetype_id: card.archetype_id }).catch(() => {});
    navigate('/create', {
      state: {
        discoverPrefill: {
          intent: { name: card.label, description: card.description },
          lens: card.lens,
          archetype_id: card.archetype_id,
        },
      },
    });
  };

  const handleOptIn = async () => {
    if (!optInName.trim() || !optInEmail.trim()) return;
    const domain = result?.domain || optInEmail.split('@')[1] || '';
    try {
      await api.post('/momentum/opt-in', {
        name: optInName, email: optInEmail, domain, consent: true,
      });
      setOptedIn(true);
    } catch {
      /* silent */
    }
  };

  const reset = () => {
    setInput('');
    setResult(null);
    setCandidates(null);
    setShowCandidates(false);
    setError(null);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const confidenceColor = (c) =>
    c >= 0.85 ? '#059669' : c >= 0.6 ? '#0A0A0A' : c >= 0.3 ? '#6B6B6B' : '#999999';

  const SectorIcon = result?.sector ? SECTOR_ICONS[result.sector] || Globe : Globe;

  return (
    <section
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{ background: 'var(--g-bg-page, #FAFAFA)' }}
      data-testid="public-discover"
    >
      {/* Ambient gradient orbs — cinematic */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]">
        <div
          className="absolute top-1/4 left-1/4 w-[480px] h-[480px] rounded-full blur-[120px]"
          style={{ background: 'radial-gradient(circle, rgba(205,34,0,0.18) 0%, transparent 60%)' }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[380px] h-[380px] rounded-full blur-[100px]"
          style={{ background: 'radial-gradient(circle, rgba(15,23,42,0.08) 0%, transparent 60%)' }}
        />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 text-center">
        {/* ════ HEADLINE ════ */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p
            className="text-[10px] font-mono tracking-[0.22em] mb-5 uppercase inline-flex items-center gap-2"
            style={{ color: SCARLET }}
          >
            <Sparkles size={11} /> Try Granullo Discover
          </p>
          <h2
            className="text-[38px] sm:text-[52px] font-semibold leading-[1.05] tracking-[-0.03em] mb-5"
            style={{ color: 'var(--g-text-primary, #0A0A0A)', fontFamily: '"Inter Tight", sans-serif' }}
            data-testid="discover-headline"
          >
            See what we'd catch on your website.
            <br />
            <span style={{ color: SCARLET }}>In 4 seconds.</span>
          </h2>
          <p
            className="text-[15px] leading-relaxed max-w-xl mx-auto mb-10"
            style={{ color: 'var(--g-text-secondary, #4A4A4A)' }}
          >
            Type a company name, paste a URL, or describe your organization. Granullo reads your world and surfaces the exact programs that would have prevented the most misuse.
          </p>
        </motion.div>

        {/* ════ SEARCH INPUT (centerpiece) ════ */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="relative mb-3"
        >
          <div
            className="relative max-w-xl mx-auto rounded-2xl transition-all"
            style={{
              background: '#FFFFFF',
              border: '1px solid rgba(15,23,42,0.10)',
              boxShadow: showCandidates
                ? '0 20px 60px rgba(15,23,42,0.12), 0 2px 4px rgba(15,23,42,0.05)'
                : '0 4px 20px rgba(15,23,42,0.05), 0 1px 2px rgba(15,23,42,0.04)',
            }}
          >
            <div className="flex items-center gap-3 pl-5 pr-2 py-2">
              <Search size={18} style={{ color: '#9CA3AF', flexShrink: 0 }} />
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (result) setResult(null);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                  if (e.key === 'Escape') setShowCandidates(false);
                }}
                placeholder="Try: stripe, apollo hospitals, or your own URL…"
                className="flex-1 text-[15px] bg-transparent outline-none py-3"
                style={{ color: '#0A0A0A' }}
                spellCheck={false}
                autoComplete="off"
                data-testid="public-discover-input"
              />
              {input && !loading && (
                <button
                  onClick={reset}
                  className="p-1.5 rounded-md transition-colors"
                  style={{ color: '#9CA3AF' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#0A0A0A'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
                  aria-label="Clear"
                >
                  <X size={14} />
                </button>
              )}
              <button
                onClick={handleSubmit}
                disabled={!input.trim() || loading}
                className="flex items-center gap-2 text-[13px] font-medium px-5 py-3 rounded-xl transition-all"
                style={{
                  background: input.trim() ? SCARLET : 'rgba(15,23,42,0.06)',
                  color: input.trim() ? '#FFFFFF' : '#9CA3AF',
                  border: '1px solid transparent',
                  cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                  boxShadow: input.trim() ? '0 2px 8px rgba(205,34,0,0.22)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (input.trim() && !loading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 10px 24px rgba(205,34,0,0.32)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = input.trim() ? '0 2px 8px rgba(205,34,0,0.22)' : 'none';
                }}
                data-testid="public-discover-go"
              >
                {loading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Analyzing…
                  </>
                ) : (
                  <>
                    Discover
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>

            {/* Candidate picker — appears when multiple matches */}
            <AnimatePresence>
              {showCandidates && candidates?.candidates?.length > 0 && (
                <motion.div
                  ref={candidateBoxRef}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 right-0 top-full mt-2 rounded-2xl overflow-hidden z-10"
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid rgba(15,23,42,0.10)',
                    boxShadow: '0 20px 60px rgba(15,23,42,0.14), 0 4px 12px rgba(15,23,42,0.06)',
                  }}
                  data-testid="public-discover-candidates"
                >
                  <div className="px-5 pt-3 pb-2 flex items-center justify-between">
                    <span
                      className="text-[10px] font-mono uppercase tracking-[0.16em]"
                      style={{ color: '#6B6B6B' }}
                    >
                      {candidates.candidates.length > 1 ? 'Which one?' : 'Match found'}
                    </span>
                    {resolving && (
                      <Loader2 size={11} className="animate-spin" style={{ color: '#6B6B6B' }} />
                    )}
                  </div>
                  <div>
                    {candidates.candidates.map((cand, idx) => {
                      const Icon = SECTOR_ICONS[cand.sector] || Globe;
                      return (
                        <button
                          key={cand.domain}
                          onClick={() => handlePickCandidate(cand)}
                          className="w-full flex items-center gap-3 px-5 py-3 text-left transition-colors"
                          style={{
                            borderTop: idx === 0 ? 'none' : '1px solid rgba(15,23,42,0.04)',
                            background: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(205,34,0,0.03)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          data-testid={`public-discover-candidate-${idx}`}
                        >
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{
                              background: 'rgba(205,34,0,0.06)',
                              color: SCARLET,
                            }}
                          >
                            <Icon size={15} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span
                                className="text-[14px] font-semibold truncate"
                                style={{ color: '#0A0A0A' }}
                              >
                                {cand.entity_name}
                              </span>
                              <span
                                className="text-[11px] font-mono truncate"
                                style={{ color: '#9CA3AF' }}
                              >
                                {cand.domain}
                              </span>
                            </div>
                            <div
                              className="text-[11px] truncate"
                              style={{ color: '#6B6B6B' }}
                            >
                              {(cand.sector || '').replace(/_/g, ' ')} · {cand.signals?.[0]}
                            </div>
                          </div>
                          <ArrowRight size={14} style={{ color: '#D1D5DB', flexShrink: 0 }} />
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Subtle hint line below */}
          <p
            className="text-[11px] font-mono mt-4"
            style={{ color: '#9CA3AF' }}
          >
            {loading
              ? 'Reading sector signals, matching to program archetypes…'
              : resolving
              ? 'Searching curated world models…'
              : 'Works with bare names. No .com required.'}
          </p>
        </motion.div>

        {/* ════ EXAMPLE CHIPS ════ */}
        {!result && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-2 mt-6"
          >
            {QUICK_EXAMPLES.map((ex) => (
              <button
                key={ex}
                onClick={() => {
                  setInput(ex);
                  setTimeout(() => inputRef.current?.focus(), 50);
                }}
                className="text-[11px] font-medium px-3 py-1.5 rounded-full transition-all"
                style={{
                  background: 'rgba(15,23,42,0.04)',
                  border: '1px solid rgba(15,23,42,0.06)',
                  color: '#6B6B6B',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(205,34,0,0.06)';
                  e.currentTarget.style.color = SCARLET;
                  e.currentTarget.style.borderColor = 'rgba(205,34,0,0.18)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,42,0.04)';
                  e.currentTarget.style.color = '#6B6B6B';
                  e.currentTarget.style.borderColor = 'rgba(15,23,42,0.06)';
                }}
                data-testid={`public-discover-example-${ex.replace(/\s+/g, '-')}`}
              >
                {ex}
              </button>
            ))}
          </motion.div>
        )}

        {/* ════ ERROR STATE ════ */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[12px]"
            style={{
              background: 'rgba(205,34,0,0.06)',
              color: SCARLET,
              border: '1px solid rgba(205,34,0,0.18)',
            }}
          >
            <AlertTriangle size={12} /> {error}
          </motion.div>
        )}

        {/* ════ RESULT PANEL ════ */}
        <AnimatePresence>
          {result && result.cards && result.cards.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mt-14 max-w-3xl mx-auto"
              data-testid="discover-result"
            >
              {/* Entity header — hero reveal */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="rounded-2xl p-6 mb-6 text-left"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(15,23,42,0.08)',
                  boxShadow: '0 4px 20px rgba(15,23,42,0.04)',
                }}
                data-testid="discover-intelligence-header"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${LENS_COLORS[result.entity_type] || SCARLET}10`,
                      color: LENS_COLORS[result.entity_type] || SCARLET,
                    }}
                  >
                    <SectorIcon size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap mb-1.5">
                      <h3
                        className="text-[22px] font-semibold truncate"
                        style={{ color: '#0A0A0A', fontFamily: '"Inter Tight", sans-serif', letterSpacing: '-0.02em' }}
                        data-testid="discover-entity-name"
                      >
                        {result.entity_name || 'Analyzed organization'}
                      </h3>
                      {result.confidence != null && (
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{
                            background: `${confidenceColor(result.confidence)}10`,
                            color: confidenceColor(result.confidence),
                          }}
                          data-testid="discover-confidence"
                        >
                          <Shield size={9} />
                          {Math.round(result.confidence * 100)}% confidence
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {result.sector && (
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full uppercase tracking-[0.1em]"
                          style={{
                            background: `${LENS_COLORS[result.entity_type] || '#6B6B6B'}10`,
                            color: LENS_COLORS[result.entity_type] || '#6B6B6B',
                          }}
                          data-testid="discover-sector-badge"
                        >
                          {result.sector.replace(/_/g, ' ')}
                        </span>
                      )}
                      {result.entity_type && (
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(15,23,42,0.04)', color: '#6B6B6B' }}
                        >
                          lens · {result.entity_type}
                        </span>
                      )}
                      {result.read_strategy && result.read_strategy !== 'user_text' && result.read_strategy !== 'direct_www' && result.read_strategy !== 'direct_apex' && (
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          style={{ background: 'rgba(59,130,246,0.06)', color: '#2563EB' }}
                          data-testid="discover-read-strategy"
                          title={
                            result.read_strategy === 'ddg_snippet' ? 'Site blocked direct fetch — read via DuckDuckGo search snippet' :
                            result.read_strategy === 'wikipedia'   ? 'Site blocked direct fetch — read via Wikipedia summary' :
                            result.read_strategy === 'bing_snippet'? 'Site blocked direct fetch — read via Bing search snippet' :
                            result.read_strategy === 'known_domain'? 'Curated entry from Granullo intelligence index' :
                            result.read_strategy === 'all_failed'  ? 'Could not read this site through any channel' :
                            result.read_strategy === 'direct_www+ddg' || result.read_strategy === 'direct_apex+ddg' ?
                              'Site read directly + enriched with search snippet for sparse pages' :
                            'Read via ' + result.read_strategy
                          }
                        >
                          {result.read_strategy === 'ddg_snippet' && 'via search'}
                          {result.read_strategy === 'wikipedia' && 'via Wikipedia'}
                          {result.read_strategy === 'bing_snippet' && 'via Bing'}
                          {result.read_strategy === 'known_domain' && 'curated'}
                          {result.read_strategy === 'all_failed' && 'unreadable'}
                          {(result.read_strategy === 'direct_www+ddg' || result.read_strategy === 'direct_apex+ddg') && 'enriched'}
                        </span>
                      )}
                      {result.fetch_from_cache && (
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(34,197,94,0.06)', color: '#16A34A' }}
                          data-testid="discover-cache-hit"
                          title="Cached result (24h TTL)"
                        >
                          cached
                        </span>
                      )}
                    </div>
                    {result.signals && result.signals.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="discover-signals">
                        {result.signals.slice(0, 4).map((s, i) => (
                          <span
                            key={i}
                            className="text-[10px] px-2 py-1 rounded-md"
                            style={{ background: 'rgba(15,23,42,0.03)', color: '#4A4A4A' }}
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {result.why_these_programs && (
                  <p
                    className="mt-4 text-[12px] leading-relaxed pt-4"
                    style={{
                      color: '#4A4A4A',
                      borderTop: '1px solid rgba(15,23,42,0.04)',
                    }}
                    data-testid="discover-why"
                  >
                    {result.why_these_programs}
                  </p>
                )}
                {result.structure && !result.is_fallback && (
                  <StructuralReadPanel structure={result.structure} />
                )}
                {result.signal_breakdown && !result.is_fallback && (
                  <div
                    className="mt-4 pt-4"
                    style={{ borderTop: '1px solid rgba(15,23,42,0.04)' }}
                  >
                    <SignalBreakdownBars
                      breakdown={result.signal_breakdown}
                      matches={result.signal_matches}
                    />
                  </div>
                )}
              </motion.div>

              {result.is_fallback && (
                <div
                  className="mb-5 px-4 py-2.5 rounded-lg flex items-center gap-2 text-left"
                  style={{ background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.18)' }}
                  data-testid="discover-fallback-label"
                >
                  <AlertTriangle size={12} style={{ color: '#D97706' }} />
                  <span className="text-[11px]" style={{ color: '#D97706' }}>
                    Starter programs while Discover learns your structure
                  </span>
                </div>
              )}

              {/* Program cards — staggered reveal */}
              <div className="flex items-center justify-between mb-4">
                <span
                  className="text-[10px] font-mono uppercase tracking-[0.18em]"
                  style={{ color: SCARLET }}
                >
                  <TrendingUp size={10} className="inline mr-1" />
                  {result.card_count} program{result.card_count > 1 ? 's' : ''} surfaced
                </span>
                <button
                  onClick={reset}
                  className="text-[11px] font-mono flex items-center gap-1"
                  style={{ color: '#6B6B6B', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <X size={11} /> Try another
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                {result.cards.map((card, idx) => (
                  <motion.button
                    key={idx}
                    onClick={() => handleCard(card)}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 + idx * 0.07, duration: 0.4 }}
                    whileHover={{ y: -2 }}
                    className="relative p-5 rounded-xl text-left group transition-all"
                    style={{
                      background: '#FFFFFF',
                      border: `1px solid ${card.is_fallback ? 'rgba(234,179,8,0.18)' : 'rgba(15,23,42,0.08)'}`,
                      boxShadow: '0 2px 10px rgba(15,23,42,0.04)',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(15,23,42,0.10), 0 2px 4px rgba(205,34,0,0.08)';
                      e.currentTarget.style.borderColor = 'rgba(205,34,0,0.22)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 10px rgba(15,23,42,0.04)';
                      e.currentTarget.style.borderColor = card.is_fallback ? 'rgba(234,179,8,0.18)' : 'rgba(15,23,42,0.08)';
                    }}
                    data-testid={`public-discover-card-${idx}`}
                  >
                    <div className="flex items-center gap-2 mb-2.5">
                      <span
                        className="text-[9px] font-mono uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
                        style={{
                          background: `${LENS_COLORS[card.lens]}12`,
                          color: LENS_COLORS[card.lens],
                        }}
                      >
                        {card.lens}
                      </span>
                      {card.confidence > 0 && (
                        <span className="text-[9px] font-mono" style={{ color: confidenceColor(card.confidence) }}>
                          {Math.round(card.confidence * 100)}%
                        </span>
                      )}
                      {card.is_fallback && (
                        <span
                          className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                          style={{ background: 'rgba(234,179,8,0.08)', color: '#D97706' }}
                        >
                          starter
                        </span>
                      )}
                    </div>
                    <p
                      className="text-[15px] font-semibold mb-1.5"
                      style={{ color: '#0A0A0A', letterSpacing: '-0.015em' }}
                    >
                      {card.label}
                    </p>
                    <p className="text-[12px] leading-relaxed mb-2" style={{ color: '#4A4A4A' }}>
                      {card.description}
                    </p>
                    {card.impact_frame && (
                      <p className="text-[10px] italic mb-1" style={{ color: '#6B6B6B' }}>
                        {card.impact_frame}
                      </p>
                    )}
                    <div
                      className="flex items-center gap-1 mt-3 opacity-0 transition-opacity group-hover:opacity-100"
                      style={{ color: SCARLET }}
                    >
                      <span className="text-[11px] font-semibold">Continue building</span>
                      <ArrowRight size={11} />
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Opt-in */}
              <div className="mt-10 text-center">
                {!showOptIn && !optedIn && (
                  <button
                    onClick={() => setShowOptIn(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium transition-all"
                    style={{ background: '#0A0A0A', color: '#FFFFFF' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    data-testid="download-discover-cta"
                  >
                    <Download size={13} /> Download my Discover report
                  </button>
                )}
                {showOptIn && !optedIn && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-sm mx-auto p-5 rounded-xl text-left"
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid rgba(15,23,42,0.08)',
                      boxShadow: '0 4px 20px rgba(15,23,42,0.06)',
                    }}
                    data-testid="opt-in-form"
                  >
                    <p className="text-[13px] font-medium mb-3" style={{ color: '#0A0A0A' }}>
                      Get your personalized Discover report
                    </p>
                    <input
                      value={optInName}
                      onChange={(e) => setOptInName(e.target.value)}
                      placeholder="Your name"
                      className="w-full text-[13px] mb-2 px-3 py-2 rounded-lg border outline-none"
                      style={{ borderColor: 'rgba(15,23,42,0.12)', background: '#FFFFFF', color: '#0A0A0A' }}
                      data-testid="opt-in-name"
                    />
                    <input
                      value={optInEmail}
                      onChange={(e) => setOptInEmail(e.target.value)}
                      placeholder="Work email"
                      type="email"
                      className="w-full text-[13px] mb-3 px-3 py-2 rounded-lg border outline-none"
                      style={{ borderColor: 'rgba(15,23,42,0.12)', background: '#FFFFFF', color: '#0A0A0A' }}
                      data-testid="opt-in-email"
                    />
                    <p className="text-[10px] mb-3" style={{ color: '#9CA3AF' }}>
                      Opt-in only. No spam. Insight follow-up by email.
                    </p>
                    <button
                      onClick={handleOptIn}
                      className="w-full py-2 rounded-lg text-[13px] font-medium transition-all"
                      style={{ background: '#0A0A0A', color: '#FFFFFF' }}
                      data-testid="opt-in-submit"
                    >
                      Send report
                    </button>
                  </motion.div>
                )}
                {optedIn && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-center gap-2"
                    data-testid="opt-in-success"
                  >
                    <CheckCircle size={14} style={{ color: '#059669' }} />
                    <span className="text-[13px]" style={{ color: '#059669' }}>
                      Report sent — check your inbox for the deep read.
                    </span>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
